import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Award } from 'lucide-react';
import { Button, Card, CardContent } from '@mlm/ui';
import { listMyCertificates } from '../lib/api';
import { fmtDate } from '../lib/format';

/**
 * Every certificate the member has earned, across courses they may since have
 * lost access to. The My Courses cards can only lead to a certificate the
 * member is still enrolled in; a credential outlives the enrollment.
 *
 * Its own page, and its own nav entry. As a tail section of My Courses it
 * rendered NOTHING until the first certificate existed, so a learner had no way
 * to find out where certificates would appear — and nowhere to send someone who
 * asked. The empty state below is the whole reason this is a page.
 */
export function MyCertificatesPage() {
  const q = useQuery({ queryKey: ['my-certificates'], queryFn: listMyCertificates });
  const items = q.data ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">My certificates</h1>
        <p className="text-sm text-muted-foreground">
          Certificates you have earned. Each one has a code anyone can verify.
        </p>
      </div>

      {q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center text-sm text-muted-foreground">
            <p>We couldn&apos;t load your certificates.</p>
            <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="space-y-3 py-12 text-center">
            <Award className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No certificates yet. Finish a course that issues one and it appears here.
            </p>
            <Button asChild size="sm" variant="outline">
              <Link to="/my-courses">Go to My Courses</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {items.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{c.courseTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    Issued {fmtDate(c.issuedAt)} · code{' '}
                    <span className="font-mono">{c.verificationCode}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3 text-xs font-medium">
                  <Link to={`/learn/${c.courseId}?cert=1`} className="text-primary hover:underline">
                    View
                  </Link>
                  <Link
                    to={`/verify-certificate/${c.verificationCode}`}
                    className="text-muted-foreground hover:underline"
                  >
                    Verify
                  </Link>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
